import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUploader from "@/components/ImageUploader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FloppyDisk, Star, CaretUpDown, Check } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

export default function ArticleEditor() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav = useNavigate();

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [cover, setCover] = useState(null);
  const [published, setPublished] = useState(true);
  const [publishAt, setPublishAt] = useState(""); // local-datetime string
  const [categoryId, setCategoryId] = useState("none");
  const [subCategory, setSubCategory] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [categories, setCategories] = useState([]);
  const [seriesId, setSeriesId] = useState("none");
  const [seriesList, setSeriesList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: cats } = await api.get("/categories");
        setCategories(cats);

        const { data: slist } = await api.get("/series");
        setSeriesList(slist);

        if (!editing) return;
        const { data } = await api.get(`/articles/${id}`);
        setTitle(data.title);
        setExcerpt(data.excerpt || "");
        setContent(data.content || "");
        setTags((data.tags || []).join(", "));
        setCover(data.cover_image);
        setPublished(!!data.published);
        setCategoryId(data.category_id || "none");
        setSeriesId(data.series_id || "none");
        setSubCategory(data.sub_category || "");
        setIsFeatured(!!data.is_featured);
        setMetaTitle(data.meta_title || "");
        setMetaDescription(data.meta_description || "");
        if (data.publish_at) {
          // Convert ISO -> "YYYY-MM-DDTHH:mm" for datetime-local input
          const d = new Date(data.publish_at);
          const pad = (n) => String(n).padStart(2, "0");
          setPublishAt(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
        }
      } catch (e) { setErr("Failed to load editor data"); }
    })();
  }, [id, editing]);

  const save = async () => {
    setBusy(true); setErr("");
    let publish_at = null;
    if (publishAt) {
      const d = new Date(publishAt);
      if (!isNaN(d.getTime())) publish_at = d.toISOString();
    }
    const payload = {
      title: title.trim() || "Untitled",
      content,
      excerpt: excerpt.trim(),
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      cover_image: cover,
      published,
      publish_at,
      category_id: categoryId === "none" ? null : categoryId,
      series_id: seriesId === "none" ? null : seriesId,
      sub_category: subCategory.trim() || null,
      is_featured: isFeatured,
      meta_title: metaTitle.trim() || null,
      meta_description: metaDescription.trim() || null,
    };
    try {
      if (editing) await api.put(`/articles/${id}`, payload);
      else await api.post(`/articles`, payload);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="pb-20">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8 lg:mb-10">
        <Link to="/dashboard" className="link-underline text-sm text-muted-foreground inline-flex items-center gap-2" data-testid="editor-back">
          <ArrowLeft size={14} /> Back to workspace
        </Link>
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 bg-card border border-border p-3 lg:p-0 lg:border-0 lg:bg-transparent">
          <div className="flex items-center gap-3">
            <span className="eyebrow text-[10px] text-muted-foreground flex items-center gap-1">
              <Star size={12} weight={isFeatured ? "fill" : "regular"} className={isFeatured ? "text-yellow-500" : ""} /> Featured
            </span>
            <Switch checked={isFeatured} onCheckedChange={setIsFeatured} />
          </div>
          <div className="h-4 w-px bg-border hidden sm:block" />
          <div className="flex items-center gap-3">
            <span className="eyebrow text-[10px] text-muted-foreground">Published</span>
            <Switch checked={published} onCheckedChange={setPublished} data-testid="editor-published-switch" />
          </div>
          <div className="h-4 w-px bg-border hidden sm:block" />
          <Button onClick={save} disabled={busy} className="rounded-none bg-foreground text-background hover:bg-foreground/90 flex-1 sm:flex-none" data-testid="editor-save-button">
            <FloppyDisk size={14} className="mr-1.5" /> {busy ? "Saving..." : (editing ? "Update" : "Publish")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8">
          <Input
            placeholder="Untitled essay"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="rounded-none border-0 border-b border-border h-auto px-0 font-serif text-4xl sm:text-5xl font-black tracking-tight focus-visible:ring-0 focus-visible:border-foreground bg-transparent"
            data-testid="editor-title-input"
          />
          <Textarea
            placeholder="Optional dek / excerpt — one sentence to draw the reader in."
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            className="rounded-none border-0 px-0 mt-2 text-lg italic font-serif resize-none focus-visible:ring-0 bg-transparent"
            rows={2}
            data-testid="editor-excerpt-input"
          />
          <div className="mt-6">
            <RichTextEditor value={content} onChange={setContent} />
          </div>
          {err ? <div className="text-sm text-destructive mt-3">{err}</div> : null}
        </div>
        <aside className="col-span-12 lg:col-span-4 space-y-6">
          <ImageUploader value={cover} onChange={setCover} testId="cover-uploader" />
          <div>
            <Label className="eyebrow text-muted-foreground">Tags (comma separated)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} className="rounded-none mt-2" placeholder="essay, books, poetry" data-testid="editor-tags-input" />
          </div>
          <div>
            <Label className="eyebrow text-muted-foreground">Schedule for later (optional)</Label>
            <Input
              type="datetime-local"
              value={publishAt}
              onChange={(e) => setPublishAt(e.target.value)}
              className="rounded-none mt-2"
              data-testid="editor-publish-at-input"
            />
            {publishAt && new Date(publishAt) > new Date() ? (
              <div className="text-xs text-muted-foreground mt-1">
                Will publish on {new Date(publishAt).toLocaleString()}
              </div>
            ) : null}
            {publishAt ? (
              <button
                type="button"
                onClick={() => setPublishAt("")}
                className="text-xs text-muted-foreground hover:text-destructive mt-1 link-underline"
                data-testid="editor-clear-schedule"
              >
                Clear schedule
              </button>
            ) : null}
          </div>
          <div>
            <Label className="eyebrow text-muted-foreground">Category</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between rounded-none mt-2 font-normal border-border"
                >
                  {categoryId !== "none"
                    ? categories.find((c) => c.id === categoryId)?.name
                    : "Select category..."}
                  <CaretUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0 rounded-none" align="start">
                <Command>
                  <CommandInput placeholder="Search category..." />
                  <CommandList>
                    <CommandEmpty>No category found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="none"
                        onSelect={() => {
                          setCategoryId("none");
                          setSubCategory("");
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            categoryId === "none" ? "opacity-100" : "opacity-0"
                          )}
                        />
                        None
                      </CommandItem>
                      {categories.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => {
                            setCategoryId(c.id);
                            setSubCategory("");
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              categoryId === c.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {c.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          {categoryId !== "none" && (
            <div>
              <Label className="eyebrow text-muted-foreground">Sub-category</Label>
              <Input 
                value={subCategory} 
                onChange={(e) => setSubCategory(e.target.value)} 
                className="rounded-none mt-2" 
                placeholder="Specific topic (e.g. React, UX)" 
              />
            </div>
          )}
          <div>
            <Label className="eyebrow text-muted-foreground">Part of a Series (optional)</Label>
            <select 
              value={seriesId} 
              onChange={(e) => setSeriesId(e.target.value)}
              className="w-full mt-2 rounded-none border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-foreground"
            >
              <option value="none">None</option>
              {seriesList.map(s => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="eyebrow text-muted-foreground mb-2">SEO Settings</div>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase font-bold text-muted-foreground">Meta Title</Label>
                <Input 
                  value={metaTitle} 
                  onChange={(e) => setMetaTitle(e.target.value)} 
                  placeholder={title || "Custom SEO Title"}
                  className="rounded-none h-8 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-muted-foreground">Meta Description</Label>
                <Textarea 
                  value={metaDescription} 
                  onChange={(e) => setMetaDescription(e.target.value)} 
                  placeholder={excerpt || "Custom SEO description for search engines."}
                  className="rounded-none text-xs mt-1 min-h-[60px]"
                />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <div className="eyebrow text-[10px] text-muted-foreground mb-2 italic">Search Preview</div>
              <div className="font-mono text-[10px] text-muted-foreground break-all">/{`{username}`}/{title ? title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : "your-slug"}</div>
              <div className="font-serif text-sm mt-1 text-blue-600 dark:text-blue-400 underline">{metaTitle || title || "Untitled essay"}</div>
              <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{metaDescription || excerpt || "No description provided."}</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
